// Утилиты для работы с изображениями

export const DEFAULT_AVATAR_URL = "/avatars/default-avatar.svg";
export const DEFAULT_COVER_URL = "/covers/default-cover.svg";

/**
 * Возвращает дефолтный аватар если переданный URL пустой или невалидный
 */
export const getAvatarUrl = (url?: string | null): string => {
  if (!url || url === "" || url.includes("undefined") || url.includes("null")) {
    return DEFAULT_AVATAR_URL;
  }
  return url;
};

/**
 * Возвращает дефолтный баннер если переданный URL пустой или невалидный
 */
export const getBannerUrl = (url?: string | null): string => {
  if (!url || url === "" || url.includes("undefined") || url.includes("null")) {
    return DEFAULT_COVER_URL;
  }
  return url;
};

/**
 * Проверяет, является ли URL дефолтным аватаром
 */
export const isDefaultAvatar = (url: string): boolean => {
  return url === DEFAULT_AVATAR_URL;
};

const DISCORD_CDN_HOSTS = new Set(["cdn.discordapp.com", "media.discordapp.net"]);

/** Ближайшая степень двойки ≥ n из допустимого диапазона Discord CDN (16..4096). */
function nextDiscordCdnSize(n: number): number {
  const allowed = [16, 32, 64, 128, 256, 512, 1024, 2048, 4096];
  return allowed.find((s) => s >= n) ?? 4096;
}

/**
 * Просит у Discord CDN аватар нужного размера вместо полноразмерного оригинала.
 *
 * Discord отдаёт `/avatars/{userId}/{hash}.png` в исходном разрешении (аплоад
 * пользователя, реально может быть под мегабайт), а в списках мы рисуем его
 * в 22–56px. `?size=N` — официальный параметр CDN (степени двойки 16..4096),
 * который просит у Discord уже уменьшенную и переоптимизированную картинку.
 *
 * @param displayPx CSS-размер, в котором аватар реально показан (не x2 — retina
 *   учитывается внутри).
 */
export function withDiscordAvatarSize(
  url: string | null | undefined,
  displayPx: number,
): string | undefined {
  if (!url) return undefined;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // Относительный путь (напр. дефолтный аватар из /avatars/default-avatar.svg) —
    // не Discord CDN, трогать нечего.
    return url;
  }
  if (!DISCORD_CDN_HOSTS.has(parsed.hostname)) return url;
  if (parsed.searchParams.has("size")) return url;
  const size = nextDiscordCdnSize(Math.ceil(displayPx * 2));
  parsed.searchParams.set("size", String(size));
  return parsed.toString();
}

export function isLikelyAnimatedGifUrl(url?: string | null): boolean {
  if (!url || typeof url !== "string") return false;
  const lower = url.toLowerCase();
  if (lower.includes(".gif?")) return true;
  const noQuery = lower.split("?")[0] ?? lower;
  return noQuery.endsWith(".gif");
}

/**
 * Обработчик ошибки загрузки изображения
 */
export const handleImageError = (
  event: React.SyntheticEvent<HTMLImageElement, Event>,
) => {
  const target = event.target as HTMLImageElement;
  if (target.src !== DEFAULT_AVATAR_URL) {
    target.src = DEFAULT_AVATAR_URL;
  }
};
